import { useStore } from 'hooks/useStore';
import type { UploadForecastRow, UploadRowValidation } from 'Interfaces/FDPInterfaces/unconstrainedForecastUpload';
import type { ValidationGridRow } from './UploadExcelValidation.type';

export function useUploadExcelValidation(onClose: () => void) {
  const { UnconstrainedForecastViewStore: store } = useStore();

  const gridRows: ValidationGridRow[] = store.uploadRows.map((row, i) => ({
    ...row,
    rowNumber: i + 1,
    validation: deriveRowValidation(row),
  }));

  const hasBlockingErrors = gridRows.some(r => r.validation.status === 'error');

  const summary = {
    total: gridRows.length,
    errors: gridRows.filter(r => r.validation.status === 'error').length,
  };

  return {
    gridRows,
    hasBlockingErrors,
    summary,
    planningPeriod: store.uploadPlanningPeriod,
    isCreating: store.isCreatingFromUpload,
    handleNoteChange: (rowIndex: number, value: string) =>
      store.updateUploadRowNote(rowIndex, value),
    handleDeleteRow: (rowIndex: number) => store.deleteUploadRow(rowIndex),
    handleCreate: () => store.handleCreateFromUpload(),
    handleClose: onClose,
  };
}

function deriveRowValidation(row: UploadForecastRow): UploadRowValidation {
  const messages: string[] = [];

  if (!row.ndc_valid) {
    messages.push(`NDC "${formatNDC11(row.ndc11)}" was not found.`);
  }
  if (row.duplicate) {
    messages.push(`NDC "${formatNDC11(row.ndc11)}" appears more than once in the uploaded file.`);
  }
  if (row.concept_mismatch) {
    messages.push('The provided concept is not matching with the NDC provided!');
  }

  return {
    status: messages.length > 0 ? 'error' : 'ok',
    messages,
  };
}

export function formatNDC11(ndc: string): string {
  const digits = ndc.replace(/-/g, '');
  if (digits.length !== 11) return ndc;
  return `${digits.slice(0, 5)}-${digits.slice(5, 9)}-${digits.slice(9)}`;
}

export function formatPlanningPeriod(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export function formatPeriodHeader(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}
